/**
 * 「食べに行く」ごほうびの解放状態を計算するユーティリティ。
 *
 * ゲームロジックから独立しているため、テストもしやすい。
 */

import type { StageDefinition, StageItemDefinition } from './types';

export interface RewardStatus {
  /** そのアイテムを作ったことがある。 */
  discovered: boolean;
  /** 累計スコアが解放条件を満たしている。 */
  unlocked: boolean;
  /** 今すぐ「食べに行く」モーダルを開ける。 */
  available: boolean;
}

/**
 * 指定アイテムのごほうびが現在開ける状態かを判定する。
 *
 * 条件: その料理に**出会ったこと**（NEXT に並んだ・作った）。呼び出し側は出会った料理のIDを渡す。
 *
 * 2026-09-24 変更：以前は「作った＋累計スコアが unlockScore 以上」だった。
 * 送客の入口が上手な人にしか開かず、観光の導線として弱かったので、スコアの条件を外した。
 * `unlockScore` はデータに残してあるが、判定には使わない（`totalScore` 引数も互換のため残す）。
 * reward 未定義なら常に available=false。
 * Lv.1 は最初から遊べる料理なので、常に解放済みとみなす。
 */
export function getRewardStatus(
  item: StageItemDefinition,
  unlockedItemIds: string[],
  totalScore: number,
): RewardStatus {
  const reward = item.reward;
  if (!reward) {
    return { discovered: unlockedItemIds.includes(item.id), unlocked: false, available: false };
  }

  // Lv.1 は最初から落ちてくるため「作った」とはみなさず、常に解放済みにする
  if (item.level === 1) {
    return { discovered: true, unlocked: true, available: true };
  }

  void totalScore;
  const discovered = unlockedItemIds.includes(item.id);

  return {
    discovered,
    unlocked: true,
    available: discovered,
  };
}

/**
 * ステージ全体で新しく解放されたアイテムを取得する。
 *
 * 使い方:
 * 1. ゲーム開始時に oldStatus = computeAllRewardStatus(...) を取得
 * 2. totalScore 更新後に newStatus = computeAllRewardStatus(...) を取得
 * 3. 差分を出して reward:unlock イベントを発行
 */
export function computeAllRewardStatuses(
  stage: StageDefinition,
  unlockedItemIds: string[],
  totalScore: number,
): Map<string, RewardStatus> {
  const out = new Map<string, RewardStatus>();
  for (const item of stage.items) {
    out.set(item.id, getRewardStatus(item, unlockedItemIds, totalScore));
  }
  return out;
}

/** 前回の状態から「available=true になった」アイテムだけを抽出する。 */
export function findNewlyAvailableRewards(
  stage: StageDefinition,
  previous: Map<string, RewardStatus>,
  current: Map<string, RewardStatus>,
): StageItemDefinition[] {
  const out: StageItemDefinition[] = [];
  for (const [id, status] of current) {
    if (!status.available || previous.get(id)?.available) continue;
    const item = stage.items.find((entry) => entry.id === id);
    if (item) out.push(item);
  }
  return out;
}
